/**
 * Adlib panels and actions from the production's studio config snapshot
 * (D22): the panels' buttons, the piece types' param forms, and the values a
 * form produces — checked against the param spec the production will check
 * them against (`paramSpecSchema`: string with `enum`/`maxLength`, number and
 * integer with `minimum`/`maximum`, boolean; `required` unless a default).
 *
 * The snapshot is parsed with the engine's schema (`parseStudioConfig`) once,
 * in the view; everything here reads the parsed config.
 */
import type { ParamSpec, StudioConfig } from '@acp/engine/studioConfig.js';
import type { JsonValue } from '@acp/domain/primitives.js';

export type AdlibItemView =
  | {
      readonly kind: 'piece';
      readonly label: string;
      readonly pieceType: string;
      readonly pieceTypeLabel: string;
      readonly params: Readonly<Record<string, JsonValue>>;
      readonly mode: 'now' | 'queue';
      readonly hotkey: string | null;
      /** The piece type has params to fill in (the item's own are the form's starting values). */
      readonly hasParams: boolean;
      /** The config names a piece type the snapshot lacks: shown, and disabled. */
      readonly missing: boolean;
    }
  | { readonly kind: 'action'; readonly label: string; readonly actionId: string; readonly hotkey: string | null; readonly missing: boolean };

export type AdlibPanelView = { readonly id: string; readonly label: string; readonly items: readonly AdlibItemView[] };

export type ActionView = { readonly id: string; readonly label: string; readonly steps: number };

export function adlibPanelsOf(config: StudioConfig): AdlibPanelView[] {
  return config.adlibPanels.map((panel) => ({
    id: panel.id,
    label: panel.label === '' ? panel.id : panel.label,
    items: panel.items.map((item): AdlibItemView => {
      if ('action' in item) return { kind: 'action', label: item.label, actionId: item.action, hotkey: item.hotkey, missing: !Object.hasOwn(config.actions, item.action) };
      const type = config.pieceTypes[item.pieceType];
      return {
        kind: 'piece',
        label: item.label,
        pieceType: item.pieceType,
        pieceTypeLabel: type === undefined || type.label === '' ? item.pieceType : type.label,
        params: item.params,
        mode: item.mode,
        hotkey: item.hotkey,
        hasParams: type !== undefined && Object.keys(type.params).length > 0,
        missing: type === undefined,
      };
    }),
  }));
}

export function actionsOf(config: StudioConfig): ActionView[] {
  return Object.entries(config.actions).map(([id, a]) => ({ id, label: a.label === '' ? id : a.label, steps: a.steps.length }));
}

/** Every piece type, for the free adlib form (any type, any params). */
export function pieceTypeChoices(config: StudioConfig): { readonly id: string; readonly label: string }[] {
  return Object.entries(config.pieceTypes).map(([id, t]) => ({ id, label: t.label === '' ? id : t.label }));
}

// ─── the param form ───

export type ParamField = {
  readonly name: string;
  readonly label: string;
  readonly spec: ParamSpec;
  /** What the input shows: text for string/number fields, a boolean for a checkbox. */
  readonly initial: string | boolean;
};

function initialOf(spec: ParamSpec, value: JsonValue | undefined): string | boolean {
  const v = value ?? spec.default;
  if (spec.type === 'boolean') return v === true;
  return v === null || v === undefined ? '' : typeof v === 'object' ? JSON.stringify(v) : String(v);
}

/** The form's fields for a piece type, filled from `values` (an item's params) or the specs' defaults. */
export function paramFields(specs: Readonly<Record<string, ParamSpec>>, values: Readonly<Record<string, JsonValue>>): ParamField[] {
  return Object.entries(specs).map(([name, spec]) => ({ name, label: spec.label === '' ? name : spec.label, spec, initial: initialOf(spec, values[name]) }));
}

export type ParamParse = { readonly ok: true; readonly params: Record<string, JsonValue> } | { readonly ok: false; readonly errors: Readonly<Record<string, string>> };

function parseNumber(spec: Extract<ParamSpec, { type: 'number' | 'integer' }>, raw: string): { value: number } | { error: string } {
  const n = Number(raw);
  if (!Number.isFinite(n)) return { error: 'not a number' };
  if (spec.type === 'integer' && !Number.isInteger(n)) return { error: 'a whole number' };
  if (spec.minimum !== null && n < spec.minimum) return { error: `at least ${String(spec.minimum)}` };
  if (spec.maximum !== null && n > spec.maximum) return { error: `at most ${String(spec.maximum)}` };
  return { value: n };
}

function parseString(spec: Extract<ParamSpec, { type: 'string' }>, raw: string): { value: string } | { error: string } {
  if (spec.enum !== null && !spec.enum.includes(raw)) return { error: `one of ${spec.enum.join(', ')}` };
  if (spec.maxLength !== null && raw.length > spec.maxLength) return { error: `at most ${String(spec.maxLength)} characters` };
  return { value: raw };
}

type FieldResult = { readonly value: JsonValue } | { readonly error: string } | { readonly skip: true };

/** One field's value, typed by its spec; a blank optional one is skipped. */
function parseField(field: ParamField, value: string | boolean): FieldResult {
  const { spec } = field;
  if (spec.type === 'boolean') return { value: value === true };
  const raw = typeof value === 'string' ? value : '';
  if (raw.trim() === '') return spec.required && spec.default === null ? { error: 'required' } : { skip: true };
  return spec.type === 'string' ? parseString(spec, raw) : parseNumber(spec, raw.trim());
}

/**
 * The params a filled-in form sends: each field typed by its spec. A blank
 * field is left out (the production applies the spec's default) unless it is
 * required without one — then it is an error, as the production would say.
 */
export function parseParams(fields: readonly ParamField[], raw: Readonly<Record<string, string | boolean>>): ParamParse {
  const params: Record<string, JsonValue> = {};
  const errors: Record<string, string> = {};
  for (const field of fields) {
    const result = parseField(field, raw[field.name] ?? field.initial);
    if ('error' in result) errors[field.name] = result.error;
    else if ('value' in result) params[field.name] = result.value;
  }
  return Object.keys(errors).length > 0 ? { ok: false, errors } : { ok: true, params };
}
